import { useEffect, useState } from 'react';
import FormLabel from '@mui/material/FormLabel';
import Slider, { SliderProps } from '@mui/material/Slider';
import { useAtom } from 'jotai';
import * as state from '@/state';
import { denormalizeSliderValues, normalizeSliderValues } from '@/utils';
import { BrightnessSliderTitleAndResetIcon } from './styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { DEFAULT_BRIGHTNESS, SliderType } from '@/constants';
import { Box } from '@mui/material';
import { abyssColors } from '@/theme/colors';

type BrightnessSliderProps = {
  title: string;
  disabled: boolean;
} & SliderProps;

const valuetext = (value: number) => {
  return `${value}`;
};

export const BrightnessSlider = ({ title, disabled, ...props }: BrightnessSliderProps) => {
  const [brightness, setBrightness] = useAtom<number>(state.brightness);
  const [unfinishedBrightness, setUnfinishedBrightness] = useState<number>(
    denormalizeSliderValues(brightness, SliderType.BRIGHTNESS)
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      setBrightness(normalizeSliderValues(unfinishedBrightness, SliderType.BRIGHTNESS));
    }, 200);
    return () => clearTimeout(debounce);
  }, [setBrightness, unfinishedBrightness]);

  const handleBrightnessChanged = (event: Event, value: number | number[]) => {
    setUnfinishedBrightness(value as number);
  };

  const handleReset = () => {
    setUnfinishedBrightness(denormalizeSliderValues(DEFAULT_BRIGHTNESS, SliderType.BRIGHTNESS));
  };

  return (
    <Box>
      <BrightnessSliderTitleAndResetIcon>
        <FormLabel sx={{ color: disabled ? '#CDCDCF' : abyssColors.primary[300] }}>
          {title}
        </FormLabel>
        {disabled ? (
          <AbyssIcon
            iconName="refresh-right-square-disabled"
            pointer
            onClick={handleReset}
            style={{ width: '2.4rem', height: '2.4rem' }}
          ></AbyssIcon>
        ) : (
          <AbyssIcon
            iconName="refresh-right-square"
            pointer
            onClick={handleReset}
            style={{ width: '2.4rem', height: '2.4rem' }}
          ></AbyssIcon>
        )}
      </BrightnessSliderTitleAndResetIcon>
      <Slider
        {...props}
        aria-label="Brightness"
        getAriaValueText={valuetext}
        valueLabelDisplay="auto"
        value={unfinishedBrightness}
        onChange={handleBrightnessChanged}
        size="small"
        sx={{ color: disabled ? '#CDCDCF' : abyssColors.primary[500] }}
        disabled={disabled}
      />
    </Box>
  );
};
